// ============================================================
// Ortak durum/rozet tanımları — sayfalarda tekrar eden yerel
// getXStatusBadge() fonksiyonlarının birleştirilmiş hali.
// Her domain kendi anahtar kümesini kullanır (çakışma olmasın diye
// tek bir düz map yerine ayrı registry'ler tercih edildi).
// ============================================================

export interface StatusDef {
  key: string;
  label: string;
  badgeClass: string;
  icon: string;
}

function buildRegistry(defs: StatusDef[]) {
  const map = new Map<string, StatusDef>();
  defs.forEach((d) => map.set(d.key, d));
  return {
    defs,
    get: (key: string) => map.get(key),
    badgeClass: (key: string) => map.get(key)?.badgeClass || 'badge-neutral',
    icon: (key: string) => map.get(key)?.icon || 'ri-question-line',
    label: (key: string) => map.get(key)?.label || key,
  };
}

export const PAYMENT_STATUSES: StatusDef[] = [
  { key: 'Ödendi', label: 'Ödendi', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Kısmi Ödendi', label: 'Kısmi Ödendi', badgeClass: 'badge-warning', icon: 'ri-pie-chart-2-line' },
  { key: 'Borçlu', label: 'Borçlu', badgeClass: 'badge-warning', icon: 'ri-error-warning-line' },
  { key: 'Gecikmiş', label: 'Gecikmiş', badgeClass: 'badge-danger', icon: 'ri-alarm-warning-line' },
  { key: 'İptal', label: 'İptal', badgeClass: 'badge-neutral', icon: 'ri-close-circle-line' },
];
export const paymentStatus = buildRegistry(PAYMENT_STATUSES);

export const DELIVERY_STATUSES: StatusDef[] = [
  { key: 'Planlandı', label: 'Planlandı', badgeClass: 'badge-neutral', icon: 'ri-calendar-line' },
  { key: 'Hazırlanıyor', label: 'Hazırlanıyor', badgeClass: 'badge-info', icon: 'ri-box-3-line' },
  { key: 'Teslimata Hazır', label: 'Teslimata Hazır', badgeClass: 'badge-warning', icon: 'ri-checkbox-circle-line' },
  { key: 'Yolda', label: 'Yolda', badgeClass: 'badge-info', icon: 'ri-truck-line' },
  { key: 'Ertelendi', label: 'Ertelendi', badgeClass: 'badge-warning', icon: 'ri-time-line' },
  { key: 'Teslim Edildi', label: 'Teslim Edildi', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-fill' },
  { key: 'Teslim Edilmedi', label: 'Teslim Edilmedi', badgeClass: 'badge-danger', icon: 'ri-close-circle-line' },
  { key: 'İptal', label: 'İptal', badgeClass: 'badge-neutral', icon: 'ri-close-circle-line' },
];
export const deliveryStatus = buildRegistry(DELIVERY_STATUSES);

// deliveries tablosundaki `status` kolonu İngilizce/snake_case saklıyor
// (planned/on_the_way/delivered) — sales.delivery_status'taki Türkçe
// metinlerden farklı bir alan. Aynı görsel dile (deliveryStatus) map'lenir.
const DELIVERY_CODE_TO_LABEL: Record<string, string> = {
  planned: 'Planlandı',
  on_the_way: 'Yolda',
  delivered: 'Teslim Edildi',
};
export function getDeliveryStatusDefByCode(code: string): StatusDef | undefined {
  const label = DELIVERY_CODE_TO_LABEL[code];
  return label ? deliveryStatus.get(label) : undefined;
}
export function getDeliveryLabelByCode(code: string): string {
  return DELIVERY_CODE_TO_LABEL[code] || code;
}

export const INSTALLMENT_STATUSES: StatusDef[] = [
  { key: 'Bekliyor', label: 'Bekliyor', badgeClass: 'badge-warning', icon: 'ri-time-line' },
  { key: 'Kısmi Ödendi', label: 'Kısmi Ödendi', badgeClass: 'badge-warning', icon: 'ri-pie-chart-2-line' },
  { key: 'Ödendi', label: 'Ödendi', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Gecikti', label: 'Gecikti', badgeClass: 'badge-danger', icon: 'ri-alarm-warning-line' },
  { key: 'İptal', label: 'İptal', badgeClass: 'badge-neutral', icon: 'ri-close-circle-line' },
];
export const installmentStatus = buildRegistry(INSTALLMENT_STATUSES);

export const CUSTOMER_STATUSES: StatusDef[] = [
  { key: 'Aktif', label: 'Aktif', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Borçlu', label: 'Borçlu', badgeClass: 'badge-warning', icon: 'ri-error-warning-line' },
  { key: 'Borcu Yok', label: 'Borcu Yok', badgeClass: 'badge-info', icon: 'ri-shield-check-line' },
  { key: 'Kara Liste', label: 'Kara Liste', badgeClass: 'badge-danger', icon: 'ri-forbid-line' },
  { key: 'İcrada', label: 'İcrada', badgeClass: 'badge-danger', icon: 'ri-scales-3-line' },
];
export const customerStatus = buildRegistry(CUSTOMER_STATUSES);

export const STOCK_STATUSES: StatusDef[] = [
  { key: 'Aktif', label: 'Aktif', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Pasif', label: 'Pasif', badgeClass: 'badge-neutral', icon: 'ri-pause-circle-line' },
  { key: 'Stokta Yok', label: 'Stokta Yok', badgeClass: 'badge-danger', icon: 'ri-close-circle-line' },
];
export const stockStatus = buildRegistry(STOCK_STATUSES);

export const STOCK_MOVEMENT_TYPES: StatusDef[] = [
  { key: 'Giriş', label: 'Giriş', badgeClass: 'badge-success', icon: 'ri-arrow-down-circle-line' },
  { key: 'Satış', label: 'Satış', badgeClass: 'badge-info', icon: 'ri-shopping-cart-2-line' },
  { key: 'İade', label: 'İade', badgeClass: 'badge-warning', icon: 'ri-arrow-go-back-line' },
  { key: 'Hasarlı', label: 'Hasarlı', badgeClass: 'badge-danger', icon: 'ri-alert-line' },
  { key: 'Manuel', label: 'Manuel', badgeClass: 'badge-neutral', icon: 'ri-edit-2-line' },
];
export const stockMovementType = buildRegistry(STOCK_MOVEMENT_TYPES);

export const ENFORCEMENT_STATUSES: StatusDef[] = [
  { key: 'Hazırlık Aşamasında', label: 'Hazırlık Aşamasında', badgeClass: 'badge-neutral', icon: 'ri-file-list-3-line' },
  { key: 'Avukata İletildi', label: 'Avukata İletildi', badgeClass: 'badge-info', icon: 'ri-user-star-line' },
  { key: 'İcra Başlatıldı', label: 'İcra Başlatıldı', badgeClass: 'badge-warning', icon: 'ri-scales-3-line' },
  { key: 'Ödeme Taahhüdü Alındı', label: 'Ödeme Taahhüdü Alındı', badgeClass: 'badge-info', icon: 'ri-hand-heart-line' },
  { key: 'Kısmi Ödeme', label: 'Kısmi Ödeme', badgeClass: 'badge-warning', icon: 'ri-pie-chart-2-line' },
  { key: 'Kısmi Tahsilat', label: 'Kısmi Tahsilat', badgeClass: 'badge-warning', icon: 'ri-pie-chart-2-line' },
  { key: 'Gecikmiş', label: 'Gecikmiş', badgeClass: 'badge-danger', icon: 'ri-alarm-warning-line' },
  { key: 'Masraf Bekliyor', label: 'Masraf Bekliyor', badgeClass: 'badge-info', icon: 'ri-money-dollar-circle-line' },
  { key: 'Tahsil Edildi', label: 'Tahsil Edildi', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Dosya Kapatıldı', label: 'Dosya Kapatıldı', badgeClass: 'badge-neutral', icon: 'ri-folder-close-line' },
  { key: 'İptal Edildi', label: 'İptal Edildi', badgeClass: 'badge-danger', icon: 'ri-close-circle-line' },
];
export const enforcementStatus = buildRegistry(ENFORCEMENT_STATUSES);

export const STAFF_STATUSES: StatusDef[] = [
  { key: 'Aktif', label: 'Aktif', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'İzinli', label: 'İzinli', badgeClass: 'badge-info', icon: 'ri-suitcase-line' },
  { key: 'Raporlu', label: 'Raporlu', badgeClass: 'badge-warning', icon: 'ri-file-paper-2-line' },
  { key: 'İşten Ayrıldı', label: 'İşten Ayrıldı', badgeClass: 'badge-danger', icon: 'ri-user-unfollow-line' },
  { key: 'Askıda', label: 'Askıda', badgeClass: 'badge-neutral', icon: 'ri-pause-circle-line' },
];
export const staffStatus = buildRegistry(STAFF_STATUSES);

export const ELOGO_STATUSES: StatusDef[] = [
  { key: 'Taslak', label: 'Taslak', badgeClass: 'badge-neutral', icon: 'ri-draft-line' },
  { key: 'Gönderim Bekliyor', label: 'Gönderim Bekliyor', badgeClass: 'badge-warning', icon: 'ri-time-line' },
  { key: 'Gönderildi', label: 'Gönderildi', badgeClass: 'badge-info', icon: 'ri-send-plane-line' },
  { key: 'GİB\'de İşleniyor', label: 'GİB\'de İşleniyor', badgeClass: 'badge-info', icon: 'ri-loader-2-line' },
  { key: 'Alıcıya İletildi', label: 'Alıcıya İletildi', badgeClass: 'badge-info', icon: 'ri-mail-send-line' },
  { key: 'Kabul Edildi', label: 'Kabul Edildi', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Reddedildi', label: 'Reddedildi', badgeClass: 'badge-danger', icon: 'ri-close-circle-line' },
  { key: 'Hatalı', label: 'Hatalı', badgeClass: 'badge-danger', icon: 'ri-error-warning-line' },
  { key: 'İptal', label: 'İptal', badgeClass: 'badge-neutral', icon: 'ri-close-circle-line' },
];
export const elogoStatus = buildRegistry(ELOGO_STATUSES);

// customers/[id] "Ödeme Sözleri" sekmesindeki promise.status alanı —
// payment-promises modülünün follow_up_status'undan (bkz. promiseStatus.ts)
// farklı bir alan/kelime dağarcığı kullanıyor, o yüzden ayrı registry.
export const PROMISE_RECORD_STATUSES: StatusDef[] = [
  { key: 'Bekliyor', label: 'Bekliyor', badgeClass: 'badge-neutral', icon: 'ri-time-line' },
  { key: 'Bugün Ödenecek', label: 'Bugün Ödenecek', badgeClass: 'badge-warning', icon: 'ri-calendar-event-line' },
  { key: 'Ödeme Alındı', label: 'Ödeme Alındı', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Kısmi Ödeme Yapıldı', label: 'Kısmi Ödeme Yapıldı', badgeClass: 'badge-info', icon: 'ri-pie-chart-2-line' },
  { key: 'Sözünü Tutmadı', label: 'Sözünü Tutmadı', badgeClass: 'badge-danger', icon: 'ri-user-unfollow-line' },
  { key: 'Ertelendi', label: 'Ertelendi', badgeClass: 'badge-warning', icon: 'ri-calendar-schedule-line' },
  { key: 'İptal', label: 'İptal', badgeClass: 'badge-neutral', icon: 'ri-close-circle-line' },
];
export const promiseRecordStatus = buildRegistry(PROMISE_RECORD_STATUSES);

export const QUOTE_STATUSES: StatusDef[] = [
  { key: 'Taslak', label: 'Taslak', badgeClass: 'badge-neutral', icon: 'ri-draft-line' },
  { key: 'Müşteriye Sunuldu', label: 'Müşteriye Sunuldu', badgeClass: 'badge-info', icon: 'ri-send-plane-line' },
  { key: 'Revize Bekliyor', label: 'Revize Bekliyor', badgeClass: 'badge-warning', icon: 'ri-edit-2-line' },
  { key: 'Beklemede', label: 'Beklemede', badgeClass: 'badge-neutral', icon: 'ri-time-line' },
  { key: 'Onaylandı', label: 'Onaylandı', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Reddedildi', label: 'Reddedildi', badgeClass: 'badge-danger', icon: 'ri-close-circle-line' },
  { key: 'Süresi Doldu', label: 'Süresi Doldu', badgeClass: 'badge-muted', icon: 'ri-hourglass-line' },
  { key: 'Satışa Dönüştürüldü', label: 'Satışa Dönüştürüldü', badgeClass: 'badge-success', icon: 'ri-shopping-cart-2-line' },
  { key: 'İptal Edildi', label: 'İptal Edildi', badgeClass: 'badge-neutral', icon: 'ri-close-circle-line' },
];
export const quoteStatus = buildRegistry(QUOTE_STATUSES);

export const EXPENSE_STATUSES: StatusDef[] = [
  { key: 'Ödendi', label: 'Ödendi', badgeClass: 'badge-success', icon: 'ri-checkbox-circle-line' },
  { key: 'Bekliyor', label: 'Bekliyor', badgeClass: 'badge-warning', icon: 'ri-time-line' },
];
export const expenseStatus = buildRegistry(EXPENSE_STATUSES);
